import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Button, TextField, Grid, Typography, FormControl, InputLabel, Select, MenuItem, Box, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { COMPANY_TYPE, companyTypeOptions } from '../../../utils/Enums';

const AddCompanyModal = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();

  const [company, setCompany] = useState({
    name: '',
    domain: '',
    siteDomains: '',
    templateId: 'basico',
    companyType: COMPANY_TYPE.FOOD_SERVICE
  });

  const [siteDomainsInput, setSiteDomainsInput] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);

  const templateOptions = [
    { value: 'basico', label: 'Básico' },
    { value: 'ecommerce1', label: 'Ecommerce 1' },
    { value: 'ecommerce2', label: 'Ecommerce 2' }
  ];

  const handleFieldChange = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setApiError('');
  };

  const handleSiteDomainsChange = (value) => {
    setSiteDomainsInput(value);
    // Convertir a formato con ; para guardar
    const domains = value.split(';').map(d => d.trim()).filter(d => d);
    handleFieldChange('siteDomains', domains.join(';'));
    setErrors(prev => ({ ...prev, siteDomains: '' }));
  };

  const validateFields = () => {
    const newErrors = {};

    if (!company.name || company.name.trim() === '') {
      newErrors.name = t('company_name_required');
    }

    if (!company.domain || company.domain.trim() === '') {
      newErrors.domain = t('domain_required');
    }

    if (!company.templateId) {
      newErrors.templateId = t('template_required') || 'Template requerido';
    }

    if (!company.companyType) {
      newErrors.companyType = t('company_type_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateFields()) {
      setSaving(true);
      try {
        await onSave(company);
        setCompany({
          name: '',
          domain: '',
          siteDomains: '',
          templateId: 'basico',
          companyType: COMPANY_TYPE.FOOD_SERVICE
        });
        setSiteDomainsInput('');
        setErrors({});
        setApiError('');
      } catch (error) {
        console.error('Error saving company:', error);
        setApiError(error?.response?.data?.error_message || 'No se pudo crear la empresa. Revisá los datos e intentá nuevamente.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleClose = () => {
    setCompany({
      name: '',
      domain: '',
      siteDomains: '',
      templateId: 'basico',
      companyType: COMPANY_TYPE.FOOD_SERVICE
    });
    setSiteDomainsInput('');
    setErrors({});
    setApiError('');
    onClose();
  };

  const siteDomains = siteDomainsInput.split(';').map(d => d.trim()).filter(d => d);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('add_company')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              {t('basic_data')}
            </Typography>
          </Grid>
          {apiError && (
            <Grid item xs={12}>
              <Alert severity="error">{apiError}</Alert>
            </Grid>
          )}
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('company_name')}
              fullWidth
              value={company.name}
              onChange={e => handleFieldChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.companyType} required>
              <InputLabel>{t('company_type')}</InputLabel>
              <Select
                value={company.companyType}
                onChange={e => handleFieldChange('companyType', e.target.value)}
                label={t('company_type')}
              >
                {companyTypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {t(option.label.toLowerCase().replace(' ', '_')) || option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.companyType && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.companyType}
              </Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('domain') || 'Dominio (Login)'}
              fullWidth
              value={company.domain}
              onChange={e => handleFieldChange('domain', e.target.value)}
              error={!!errors.domain}
              helperText={errors.domain || 'Dominio único para login'}
              placeholder="ejemplo.com"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.templateId} required>
              <InputLabel>{t('template_id') || 'Template'}</InputLabel>
              <Select
                value={company.templateId}
                onChange={e => handleFieldChange('templateId', e.target.value)}
                label={t('template_id') || 'Template'}
              >
                {templateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.templateId && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.templateId}
              </Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('site_domains') || 'Dominios del Sitio Web'}
              fullWidth
              multiline
              rows={3}
              value={siteDomainsInput}
              onChange={e => handleSiteDomainsChange(e.target.value)}
              error={!!errors.siteDomains}
              helperText={errors.siteDomains || 'Separar múltiples dominios con punto y coma (;). Opcional.'}
              placeholder="ejemplo.com; www.ejemplo.com; otro-dominio.com"
            />
            {siteDomains.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {siteDomains.map((domain, idx) => (
                  <Chip key={idx} label={domain} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <LoadingButton onClick={handleSave} variant="contained" loading={saving}>
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddCompanyModal;
